import { i18n } from '@/locales';

/**
 * Backend domain errors expose a stable, machine-readable shape:
 *
 *   {
 *     code: 'CONFLICT' | 'NOT_FOUND' | 'VALIDATION_ERROR' | …,
 *     message: 'Human-readable but English-only',
 *     errors?: {
 *       conflictingResource?: { entity, field, value },
 *       …
 *     }
 *   }
 *
 * `translateApiError` keys off `code` (and the structured payload inside
 * `errors`) to produce a localized message in the active locale. The
 * backend message is the final fallback when no specific mapping fits —
 * better surface its English copy than leave the user with "an
 * unexpected error".
 *
 * Single point of i18n for API errors: when a new domain error lands on
 * the backend, add the matching key under `errors.api.*` in en/es/pt and
 * extend the switch below. Callers (services via `mapApiError`) stay
 * untouched.
 */

interface ApiErrorPayload {
  code?: string;
  message?: string;
  errors?: {
    conflictingResource?: { entity?: string; field?: string; value?: unknown };
    [key: string]: unknown;
  };
}

interface AxiosLikeError {
  isAxiosError?: boolean;
  response?: { status?: number; data?: ApiErrorPayload | string | null };
  message?: string;
}

const SERVER_ERROR_MIN_STATUS = 500;

const ENTITY_KEY: Record<string, string> = {
  Patient: 'errors.api.entity.patient',
  Appointment: 'errors.api.entity.appointment',
  Invoice: 'errors.api.entity.invoice',
  Encounter: 'errors.api.entity.encounter',
  User: 'errors.api.entity.user',
  Hospital: 'errors.api.entity.hospital',
  Organization: 'errors.api.entity.organization',
  Doctor: 'errors.api.entity.doctor',
  Nurse: 'errors.api.entity.nurse',
};

const FIELD_KEY: Record<string, string> = {
  'email': 'errors.api.field.email',
  'phone': 'errors.api.field.phone',
  'nationalId': 'errors.api.field.nationalId',
  'phone/email/nationalId': 'errors.api.field.contact',
};

function tr(key: string, fallback?: string): string {
  // Vue-i18n's global `t` returns the key itself when the key is missing.
  // Guard against that by comparing — if the locale lookup didn't resolve,
  // use the explicit fallback (typically the backend's English string).
  const t = i18n.global.t as (key: string, named?: Record<string, unknown>) => string;
  const out = t(key);
  return out && out !== key ? out : (fallback ?? out);
}

function trFmt(key: string, named: Record<string, unknown>, fallback?: string): string {
  const t = i18n.global.t as (key: string, named?: Record<string, unknown>) => string;
  const out = t(key, named);
  return out && out !== key ? out : (fallback ?? out);
}

/** True when the request never got a response (no connection, aborted), as opposed to an error the server answered with. */
export function isConnectionError(err: unknown): boolean {
  const requestError = err as AxiosLikeError | undefined;
  return Boolean(requestError?.isAxiosError && !requestError.response);
}

export function translateApiError(err: AxiosLikeError | unknown): string {
  const requestError = err as AxiosLikeError | undefined;
  // Axios's own texts ("Network Error", "Request failed with status code 503") are never shown.
  if (isConnectionError(err)) {
    return tr('errors.api.network', 'Could not connect to the server');
  }
  const payload = extractPayload(err);
  const backendMessage = payload?.message ?? (requestError?.isAxiosError ? undefined : requestError?.message);

  const domainMessage = payload?.code ? translateDomainError(payload.code, payload, backendMessage) : null;
  if (domainMessage !== null) return domainMessage;
  if ((requestError?.response?.status ?? 0) >= SERVER_ERROR_MIN_STATUS) {
    return tr('errors.api.internal', 'A server error occurred');
  }
  return backendMessage ?? tr('errors.api.unexpected', 'An unexpected error occurred');
}

// The message of a known domain error code, or null when the code is not one of them.
function translateDomainError(code: string, payload: ApiErrorPayload, backendMessage: string | undefined): string | null {
  switch (code) {
    case 'CONFLICT': {
      const c = payload.errors?.conflictingResource;
      if (c?.entity && c.field) {
        const entityLabel = tr(ENTITY_KEY[c.entity] ?? '', c.entity);
        const fieldLabel = tr(FIELD_KEY[c.field] ?? '', c.field);
        return trFmt('errors.api.conflict.alreadyExists', { entity: entityLabel, field: fieldLabel, value: String(c.value ?? '') }, backendMessage);
      }
      return tr('errors.api.conflict.generic', backendMessage);
    }
    case 'NOT_FOUND':
      return tr('errors.api.notFound', backendMessage);
    case 'VALIDATION_ERROR':
    case 'REQUIRED_FIELD':
    case 'INVALID_FORMAT':
      return backendMessage ?? tr('errors.api.validation', 'Validation failed');
    case 'BAD_REQUEST':
      // Class-validator failures bubble up with `code: BAD_REQUEST` and the
      // sentinel message "Validation failed" from the domain-exception filter
      // (see apps/backend/.../filters/domain-exception.filter.ts). The sentinel
      // is English-only and not informative, so swap it for the translated
      // generic message. Pass through other 400 messages (which are typically
      // specific business errors worth surfacing).
      if (backendMessage === 'Validation failed') {
        return tr('errors.api.validation', 'Validation failed');
      }
      return backendMessage ?? tr('errors.api.validation', 'Validation failed');
    case 'BUSINESS_RULE_VIOLATION':
      return backendMessage ?? tr('errors.api.businessRule', 'Operation not allowed');
    case 'INVALID_CREDENTIALS':
      return tr('errors.api.invalidCredentials', backendMessage);
    case 'INACTIVE_ACCOUNT':
      return tr('errors.api.inactiveAccount', backendMessage);
    case 'INVALID_TOKEN':
      return tr('errors.api.invalidToken', backendMessage);
    case 'ACCESS_DENIED':
      return tr('errors.api.accessDenied', backendMessage);
    case 'CALENDAR_PROVIDER_NOT_CONFIGURED':
      // CORA-300 — raced connect against a provider whose OAuth config is
      // absent (the UI normally disables Connect before this can happen).
      return tr('errors.api.calendarProviderNotConfigured', backendMessage);
    case 'TREATMENT_ORDER_NOT_REQUESTED':
      return tr('errors.api.treatmentOrderNotRequested', backendMessage);
    case 'TREATMENT_ORDER_REBOOKED_OR_CLOSED':
      return tr('errors.api.treatmentOrderRebookedOrClosed', backendMessage);
    case 'APPOINTMENT_ALREADY_STARTED':
      return tr('errors.api.appointmentAlreadyStarted', backendMessage);
    case 'APPOINTMENT_SLOT_TAKEN':
      return tr('errors.api.appointmentSlotTaken', backendMessage);
    case 'INTERNAL_ERROR':
      return tr('errors.api.internal', backendMessage ?? 'Internal server error');
    default:
      return null;
  }
}

function extractPayload(err: unknown): ApiErrorPayload | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as AxiosLikeError;
  const data = e.response?.data;
  if (!data || typeof data !== 'object') return null;
  return data;
}
